import {
  Component,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { SearchSubmitMethod } from '@app/search/services/search-tracker.service';
import { SimpleItemViewModel } from '@app/shared/models/core-view.model';
import { getDeepCopy } from '@app/shared/utils/property';
import { TagsService } from '@app/tags/services/tags.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface SkillsMatchViewModel
  extends SimpleItemViewModel<TagsApi.TagDetails> {}

@Component({
  selector: 'dgx-search-skills',
  templateUrl: './search-skills.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchSkillsComponent {
  @Input() public items: SkillsMatchViewModel[];
  @Input() public isLoading: boolean;
  @Input() public totalNumItems: number;
  @Input() public searchTerm: string;

  public i18n = this.translateService.instant([
    'LearningSearch_Skills',
    'dgTagRating_RemoveA11y',
    'dgTagRating_AddA11y',
    'dgTagRating_AddToProfileTooltip',
    'dgTagRating_RemoveFromProfileTooltip',
  ]);

  constructor(
    private tagsService: TagsService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  public get linkData() {
    return {
      route: '/learning/skills',
      searchTerm: this.searchTerm,
      collectionLink: SearchSubmitMethod.moreSkills,
    };
  }

  public get showNoResults(): boolean {
    return this.totalNumItems === 0 && !this.isLoading;
  }

  public get showError(): boolean {
    return !this.items && !this.isLoading;
  }

  public getButtonIcon(isFollowing: boolean): string {
    return isFollowing ? 'checkmark' : 'plus';
  }

  public getButtonAriaLabel(item: TagsApi.TagDetails): string {
    const followText = item.isFollowing
      ? this.i18n.dgTagRating_RemoveA11y
      : this.i18n.dgTagRating_AddA11y;
    return `${followText}: ${item.title}`;
  }

  public getButtonType(isFollowing: boolean) {
    return isFollowing ? 'primary' : 'passive';
  }

  public doFollowAction(item: SimpleItemViewModel<TagsApi.TagDetails>): void {
    // Cache item so we can easily revert on errors
    const cachedItem = getDeepCopy(item);
    const isFollowing = cachedItem.model.isFollowing;
    const action = isFollowing ? 'removeUserTag' : 'addUserTag';
    const explainText = isFollowing
      ? this.i18n.dgTagRating_AddToProfileTooltip
      : this.i18n.dgTagRating_RemoveFromProfileTooltip;

    // Immediately update UI for better user experience
    this.updateItem(item.model.tagId, !isFollowing, explainText);

    this.tagsService[action](item.model)
      .pipe(
        catchError((error) => {
          // Revert changes if request fails
          this.updateItem(
            cachedItem.model.tagId,
            cachedItem.model.isFollowing,
            cachedItem.explainText
          );
          this.cdr.markForCheck();
          return of(error);
        })
      )
      .subscribe();
  }

  public trackByFn(
    index: number,
    item: SimpleItemViewModel<TagsApi.TagDetails>
  ): number {
    return item.model.tagId;
  }

  private updateItem(
    tagId: number,
    isFollowing: boolean,
    explainText: string
  ): void {
    this.items = this.items.map((item) => {
      if (item.model.tagId === tagId) {
        return {
          ...item,
          explainText,
          model: {
            ...item.model,
            isFollowing,
          },
        };
      }
      return item;
    });
  }
}
